import { CloseOutlined, Search } from "@mui/icons-material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import Skeleton from "@mui/material/Skeleton";
import { Theme, ThemeProvider } from "@mui/material/styles";
import React from "react";
import type { JSX } from "react";
import {
    CustomItemFooter,
    CustomItemFooterText,
    CustomItemHeader,
    CustomListItemText,
    CustomSubtleText,
    CustomTextField,
} from "./stylesheet";
import { useOrganizations } from "../../hooks";
import { SearchContext } from "../../providers/SearchManagerProvider";
import WeaveBrandTheme from "../../themes/WeaveBrandTheme";
import GlobalSearchResultItem from "../GlobalSearchResultItem/GlobalSearchResultItem";
import OrganizationSelector from "../OrganizationSelector/OrganizationSelector";
import { RenderTagFunc } from "../SearchResultsTag/SearchResultsTag";

export interface GlobalSearchFlyoutProps {
    hideOrgSelector?: boolean;
    renderTags?: RenderTagFunc;
    onCloseClicked: () => void;
    onLinkClicked: (resourceId: string | undefined, tagIds: string[] | undefined) => void;
    onTagLinkClicked: (tagId: string) => void;
    onSearchAllButtonClicked: (query: string) => void;
}

export default function GlobalSearchFlyout(props: GlobalSearchFlyoutProps): JSX.Element {
    const searchManager = React.useContext(SearchContext);

    const organisations = useOrganizations();
    const selectedOrgName = searchManager.organizationsSelected.at(0);

    function handleOrganizationChanged(organization: string): void {
        searchManager.setOrganizationsSelected([organization]);
    }

    function renderSearchResult(): JSX.Element {
        if (searchManager.query === "") {
            return <></>;
        }

        if (!searchManager.loading && searchManager.results.length === 0) {
            return <CustomSubtleText>No results</CustomSubtleText>;
        }

        return (
            <List component="div" aria-label="search results" sx={{ p: 0 }}>
                <CustomItemHeader onClick={() => props.onSearchAllButtonClicked(searchManager.query)}>
                    <Search sx={{ fontSize: 20, mr: 1 }} />
                    <CustomListItemText
                        primary={searchManager.query || "Search query"}
                        secondary="Search schemas in Autodesk"
                    />
                    <OpenInNewIcon sx={{ fontSize: 20, ml: 1, color: "#666666" }} />
                </CustomItemHeader>
                {searchManager.results.map((option) => (
                    <GlobalSearchResultItem
                        key={option.searchResultId?.id}
                        query={searchManager.query}
                        searchData={option}
                        onLinkClicked={props.onLinkClicked}
                        onTagLinkClicked={props.onTagLinkClicked}
                        renderTags={props.renderTags}
                    />
                ))}
                <CustomItemFooter onClick={() => props.onSearchAllButtonClicked(searchManager.query)}>
                    <CustomItemFooterText primary={`Show all ${searchManager.total || "0"} results`} />
                </CustomItemFooter>
            </List>
        );
    }

    function renderLoadingSkeleton(): JSX.Element {
        return (
            <Box sx={{ padding: "1.8rem" }}>
                <Skeleton animation="wave" width="50%" sx={{ mb: 1 }} />
                <Skeleton animation="wave" width="30%" sx={{ mb: 1 }} />
                <Skeleton animation="wave" width="60%" sx={{ mb: 1 }} />
                <Skeleton animation="wave" width="80%" />
            </Box>
        );
    }

    return (
        <ThemeProvider theme={WeaveBrandTheme.light}>
            <CustomTextField
                id="search-textbox"
                name="search-textbox"
                placeholder="Search schemas"
                fullWidth
                value={searchManager.query}
                onChange={(event) => searchManager.setQuery(event.target.value)}
                autoFocus
                variant="standard"
                slotProps={{
                    input: {
                        autoComplete: "off",
                        startAdornment: (
                            <InputAdornment position="start" sx={{ m: 0 }}>
                                <Search fontSize="medium" sx={{ marginRight: "0.5rem" }} />
                                {!props.hideOrgSelector && (
                                    <OrganizationSelector
                                        organizations={organisations.data || []}
                                        organizationName={selectedOrgName}
                                        onOrganizationChanged={handleOrganizationChanged}
                                    />
                                )}
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={props.onCloseClicked}
                                    edge="end"
                                    sx={(theme: Theme) => ({
                                        backgroundColor: `${theme.palette.common.white} !important`,
                                        color: `${theme.palette.grey[500]} !important`,
                                        border: 0,
                                        width: "30px !important",
                                        height: "30px !important",
                                        borderRadius: "50%",
                                        margin: "2px",
                                    })}
                                >
                                    <CloseOutlined
                                        sx={(theme: Theme) => ({ color: `${theme.palette.common.black} !important` })}
                                    />
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
            />
            {searchManager.loading ? renderLoadingSkeleton() : renderSearchResult()}
        </ThemeProvider>
    );
}
